import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import {
  api, errorMessage,
  type PolicyTemplateRow, type PolicyTemplateType,
  type DeliveryRow, type DeliveryBody,
  type AddressCell, type AddressBody,
  type NoticeItem, type NoticeBody,
  type PolicyTemplateCreateInput, type PolicyTemplatePatchInput,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useI18n } from '@/lib/i18n'
import { PageError, PageLoading } from './Stats'

const TYPES: PolicyTemplateType[] = ['delivery', 'address', 'notice']

export function PoliciesScreen() {
  const toast = useToast()
  const { t } = useI18n()
  const [filter, setFilter] = useState<PolicyTemplateType | 'all'>('all')
  const [rows, setRows] = useState<PolicyTemplateRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<EditingPolicy | null>(null)
  const [deleting, setDeleting] = useState<PolicyTemplateRow | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  function load() {
    setBusy(true); setError(null)
    api.listPolicyTemplates(filter === 'all' ? undefined : filter)
      .then((r) => setRows(r.templates))
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }
  useEffect(() => { load() }, [filter])

  async function confirmDelete() {
    if (!deleting) return
    setDeleteBusy(true)
    try {
      await api.deletePolicyTemplate(deleting.id)
      toast.success(deleting.slug, t('policies.toast.deleted'))
      setDeleting(null)
      load()
    } catch (e) {
      toast.error(t('policies.toast.deleteFailed'), errorMessage(e))
    } finally { setDeleteBusy(false) }
  }

  async function save(form: EditingPolicy) {
    try {
      // For address templates, drop empty cells before submit so the server's
      // strict zod (label.min(1) / value.min(1)) doesn't reject a "markdown
      // mode" save where the cells panel was left untouched. Same idea for
      // pre-existing partially-filled rows the user added but never filled in.
      const sanitizedBody = (form.type === 'address')
        ? (() => {
            const b = form.body as AddressBody
            const filterCells = (arr: AddressCell[] = []) =>
              arr.filter((c) => c.label.trim() !== '' && c.value.trim() !== '')
            const cellsI18n: Record<string, AddressCell[]> = {}
            for (const [lang, cells] of Object.entries(b.cellsI18n ?? {})) {
              cellsI18n[lang] = filterCells(cells)
            }
            return { ...b, cellsI18n }
          })()
        : form.body
      if (form.id) {
        const patch: PolicyTemplatePatchInput = { slug: form.slug, titleI18n: form.titleI18n, body: sanitizedBody }
        await api.patchPolicyTemplate(form.id, patch)
        toast.success(form.slug, t('policies.toast.updated'))
      } else {
        const create = { type: form.type, slug: form.slug, titleI18n: form.titleI18n, body: sanitizedBody } as PolicyTemplateCreateInput
        await api.createPolicyTemplate(create)
        toast.success(form.slug, t('policies.toast.created'))
      }
      setEditing(null)
      load()
    } catch (e) {
      toast.error(t('policies.toast.saveFailed'), errorMessage(e))
    }
  }

  if (error) return <PageError msg={error}/>
  if (!rows && busy) return <PageLoading/>

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('policies.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('policies.subtitle', { n: rows?.length ?? 0 })}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="h-9 rounded-md border border-border bg-card px-3 text-sm"
          >
            <option value="all">{t('policies.filter.all')}</option>
            {TYPES.map((tp) => <option key={tp} value={tp}>{t(`policies.type.${tp}` as any)}</option>)}
          </select>
          <Button onClick={() => setEditing(blankPolicy('delivery'))}>
            <Plus size={14} strokeWidth={2}/> {t('policies.action.new')}
          </Button>
        </div>
      </header>

      <Table>
        <THead>
          <Tr>
            <Th>{t('policies.col.type')}</Th>
            <Th>{t('policies.col.slug')}</Th>
            <Th>{t('policies.col.title')}</Th>
            <Th className="text-right pr-4">{t('policies.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows?.map((p) => (
            <Tr key={p.id}>
              <Td><Badge>{t(`policies.type.${p.type}` as any)}</Badge></Td>
              <Td className="font-mono text-xs">{p.slug}</Td>
              <Td className="font-medium">{p.titleI18n.zh ?? p.titleI18n.en ?? p.slug}</Td>
              <Td className="text-right space-x-1.5 whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => setEditing(toEditing(p))}>{t('action.edit')}</Button>
                <Button size="sm" variant="danger" onClick={() => setDeleting(p)}>{t('action.delete')}</Button>
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      {editing && (
        <PolicyEditor
          value={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => save(editing)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('policies.confirmDelete.title')}
        message={deleting ? t('policies.confirmDelete.body', { slug: deleting.slug }) : ''}
        confirmLabel={t('policies.confirmDelete.confirm')}
        cancelLabel={t('policies.confirmDelete.cancel')}
        tone="danger"
        busy={deleteBusy}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

// ─── Editor ───────────────────────────────────────────────────

interface EditingPolicy {
  id?: string
  type: PolicyTemplateType
  slug: string
  titleI18n: { zh?: string; en?: string }
  body: DeliveryBody | AddressBody | NoticeBody
}

function blankPolicy(type: PolicyTemplateType): EditingPolicy {
  return { type, slug: '', titleI18n: { zh: '', en: '' }, body: blankBody(type) }
}

function blankBody(type: PolicyTemplateType): DeliveryBody | AddressBody | NoticeBody {
  if (type === 'delivery') return { rowsI18n: { zh: [], en: [] }, hintI18n: { zh: '', en: '' } }
  if (type === 'address')  return { cellsI18n: { zh: [], en: [] }, ipNoteI18n: { zh: '', en: '' } }
  return { itemsI18n: { zh: [], en: [] } }
}

function toEditing(p: PolicyTemplateRow): EditingPolicy {
  return {
    id: p.id,
    type: p.type,
    slug: p.slug,
    titleI18n: { zh: p.titleI18n.zh ?? '', en: p.titleI18n.en ?? '' },
    body: (p.body ?? blankBody(p.type)) as any,
  }
}

function PolicyEditor({
  value, onChange, onCancel, onSave,
}: {
  value: EditingPolicy
  onChange: (v: EditingPolicy) => void
  onCancel: () => void
  onSave: () => void
}) {
  const { t } = useI18n()
  const set = <K extends keyof EditingPolicy>(k: K, v: EditingPolicy[K]) => onChange({ ...value, [k]: v })
  const setBody = (b: EditingPolicy['body']) => onChange({ ...value, body: b })

  const canSave = !!value.slug.trim() && !!(value.titleI18n.zh || value.titleI18n.en)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <header className="flex items-center justify-between sticky top-0 bg-card z-10 -mx-5 -mt-5 px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-lg font-semibold tracking-tight">
            {value.id ? t('policies.editor.titleEdit') : t('policies.editor.titleNew')}
          </h2>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X size={18}/>
          </button>
        </header>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">{t('policies.field.type')}</label>
            <select
              value={value.type}
              disabled={!!value.id}
              onChange={(e) => {
                const next = e.target.value as PolicyTemplateType
                onChange({ ...value, type: next, body: blankBody(next) })
              }}
              className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm disabled:opacity-50"
            >
              {TYPES.map((tp) => <option key={tp} value={tp}>{t(`policies.type.${tp}` as any)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t('policies.field.slug')}</label>
            <Input
              value={value.slug}
              onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="openai-plus-delivery"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t('policies.field.titleZh')}</label>
            <Input value={value.titleI18n.zh ?? ''}
              onChange={(e) => set('titleI18n', { ...value.titleI18n, zh: e.target.value })}/>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t('policies.field.titleEn')}</label>
            <Input value={value.titleI18n.en ?? ''}
              onChange={(e) => set('titleI18n', { ...value.titleI18n, en: e.target.value })}/>
          </div>
        </div>

        {value.type === 'delivery' && (
          <DeliveryBodyEditor body={value.body as DeliveryBody} onChange={setBody}/>
        )}
        {value.type === 'address' && (
          <AddressBodyEditor body={value.body as AddressBody} onChange={setBody}/>
        )}
        {value.type === 'notice' && (
          <NoticeBodyEditor body={value.body as NoticeBody} onChange={setBody}/>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
          <Button onClick={onSave} disabled={!canSave}>{t('action.save')}</Button>
        </div>
      </div>
    </div>
  )
}

// ─── Per-type body editors ─────────────────────────────────────

function DeliveryBodyEditor({ body, onChange }: { body: DeliveryBody; onChange: (b: DeliveryBody) => void }) {
  const { t } = useI18n()
  const setRows = (lang: 'zh' | 'en', rows: DeliveryRow[]) =>
    onChange({ ...body, rowsI18n: { ...body.rowsI18n, [lang]: rows } })
  const setHint = (lang: 'zh' | 'en', v: string) =>
    onChange({ ...body, hintI18n: { ...(body.hintI18n ?? {}), [lang]: v } })

  return (
    <div className="space-y-4">
      {(['zh', 'en'] as const).map((lang) => (
        <div key={lang} className="border border-border rounded-md p-3 space-y-2">
          <div className="text-xs font-medium uppercase text-muted-foreground tracking-wider">{lang}</div>
          <RowsList
            rows={body.rowsI18n[lang] ?? []}
            onChange={(rs) => setRows(lang, rs)}
            keyLabel={t('policies.delivery.key')}
            valueLabel={t('policies.delivery.value')}
            addLabel={t('policies.delivery.addRow')}
          />
          <label className="text-xs text-muted-foreground">{t('policies.delivery.hint')}</label>
          <textarea
            value={body.hintI18n?.[lang] ?? ''}
            onChange={(e) => setHint(lang, e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
          />
        </div>
      ))}
    </div>
  )
}

function RowsList({
  rows, onChange, keyLabel, valueLabel, addLabel,
}: {
  rows: DeliveryRow[]
  onChange: (rs: DeliveryRow[]) => void
  keyLabel: string
  valueLabel: string
  addLabel: string
}) {
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_2fr_auto_auto] gap-1.5 items-center">
          <Input value={r.key} placeholder={keyLabel}
            onChange={(e) => onChange(rows.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))}/>
          <Input value={r.value} placeholder={valueLabel}
            onChange={(e) => onChange(rows.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))}/>
          <select
            value={r.tone ?? ''}
            onChange={(e) => onChange(rows.map((x, idx) => idx === i ? { ...x, tone: (e.target.value || undefined) as any } : x))}
            className="h-9 rounded-md border border-border bg-card px-2 text-xs"
          >
            <option value="">—</option>
            <option value="accent">accent</option>
            <option value="muted">muted</option>
          </select>
          <Button size="sm" variant="ghost" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
            <X size={13}/>
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" type="button"
        onClick={() => onChange([...rows, { key: '', value: '' }])}>
        <Plus size={12}/> {addLabel}
      </Button>
    </div>
  )
}

function AddressBodyEditor({ body, onChange }: { body: AddressBody; onChange: (b: AddressBody) => void }) {
  const { t } = useI18n()
  const setCells = (lang: 'zh' | 'en', cells: AddressCell[]) =>
    onChange({ ...body, cellsI18n: { ...body.cellsI18n, [lang]: cells } })
  const setNote = (lang: 'zh' | 'en', v: string) =>
    onChange({ ...body, ipNoteI18n: { ...(body.ipNoteI18n ?? {}), [lang]: v } })
  // Markdown override — when this is non-empty for a locale, the storefront
  // renders it as rich text instead of the cells/ipNote layout. Lets admins
  // ship long-form 使用说明 without redesigning a structured shape.
  const setMd = (lang: 'zh' | 'en', v: string) =>
    onChange({ ...body, markdownI18n: { ...(body.markdownI18n ?? {}), [lang]: v } })

  return (
    <div className="space-y-4">
      {(['zh', 'en'] as const).map((lang) => {
        const cells = body.cellsI18n[lang] ?? []
        const md = body.markdownI18n?.[lang] ?? ''
        const mdActive = md.trim().length > 0
        return (
          <div key={lang} className="border border-border rounded-md p-3 space-y-2">
            <div className="text-xs font-medium uppercase text-muted-foreground tracking-wider">{lang}</div>

            {/* Markdown override sits on top — if it's set, the cells below
                won't render on the storefront, so we dim them visually as a
                hint without removing the inputs (admin may want to keep
                cells as a back-up draft). */}
            <label className="text-xs text-muted-foreground">{t('policies.address.markdown')}</label>
            <textarea
              value={md}
              onChange={(e) => setMd(lang, e.target.value)}
              rows={mdActive ? 14 : 4}
              placeholder={t('policies.address.markdownPlaceholder')}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono leading-relaxed"
            />
            {mdActive && (
              <p className="text-[11px] text-warning">
                {t('policies.address.markdownActiveHint')}
              </p>
            )}

            <div className={mdActive ? 'opacity-50 pointer-events-none space-y-1.5' : 'space-y-1.5'}>
              {cells.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.4fr_2fr_auto] gap-1.5 items-center">
                  <Input value={c.label} placeholder={t('policies.address.label')}
                    onChange={(e) => setCells(lang, cells.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}/>
                  <Input value={c.value} placeholder={t('policies.address.value')}
                    onChange={(e) => setCells(lang, cells.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))}/>
                  <Input value={c.sub ?? ''} placeholder={t('policies.address.sub')}
                    onChange={(e) => setCells(lang, cells.map((x, idx) => idx === i ? { ...x, sub: e.target.value } : x))}/>
                  <Button size="sm" variant="ghost" onClick={() => setCells(lang, cells.filter((_, idx) => idx !== i))}>
                    <X size={13}/>
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" type="button"
                onClick={() => setCells(lang, [...cells, { label: '', value: '', sub: '' }])}>
                <Plus size={12}/> {t('policies.address.addCell')}
              </Button>
            </div>
            <label className={mdActive ? 'text-xs text-muted-foreground opacity-50' : 'text-xs text-muted-foreground'}>
              {t('policies.address.ipNote')}
            </label>
            <textarea
              value={body.ipNoteI18n?.[lang] ?? ''}
              onChange={(e) => setNote(lang, e.target.value)}
              rows={2}
              disabled={mdActive}
              className={'w-full rounded-md border border-border bg-card px-3 py-2 text-sm ' + (mdActive ? 'opacity-50' : '')}
            />
          </div>
        )
      })}
    </div>
  )
}

function NoticeBodyEditor({ body, onChange }: { body: NoticeBody; onChange: (b: NoticeBody) => void }) {
  const { t } = useI18n()
  const setItems = (lang: 'zh' | 'en', items: NoticeItem[]) =>
    onChange({ ...body, itemsI18n: { ...body.itemsI18n, [lang]: items } })

  return (
    <div className="space-y-4">
      {(['zh', 'en'] as const).map((lang) => {
        const items = body.itemsI18n[lang] ?? []
        return (
          <div key={lang} className="border border-border rounded-md p-3 space-y-2">
            <div className="text-xs font-medium uppercase text-muted-foreground tracking-wider">{lang}</div>
            <div className="space-y-1.5">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr_auto] gap-1.5 items-start">
                  <select
                    value={it.kind}
                    onChange={(e) => setItems(lang, items.map((x, idx) => idx === i ? { ...x, kind: e.target.value as any } : x))}
                    className="h-9 rounded-md border border-border bg-card px-2 text-xs"
                  >
                    <option value="warn">warn</option>
                    <option value="block">block</option>
                  </select>
                  <div className="space-y-1">
                    <Input value={it.title} placeholder={t('policies.notice.itemTitle')}
                      onChange={(e) => setItems(lang, items.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))}/>
                    <textarea
                      value={it.body}
                      placeholder={t('policies.notice.itemBody')}
                      onChange={(e) => setItems(lang, items.map((x, idx) => idx === i ? { ...x, body: e.target.value } : x))}
                      rows={2}
                      className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                    />
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setItems(lang, items.filter((_, idx) => idx !== i))}>
                    <X size={13}/>
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" type="button"
                onClick={() => setItems(lang, [...items, { kind: 'warn', title: '', body: '' }])}>
                <Plus size={12}/> {t('policies.notice.addItem')}
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
